import {
  injectable,
  inject,
  IPageSetupData,
  IPageParseService,
  IProjectService,
  postConstruct,
} from '@meego-remote/core/app/contract';
import { DetailPageKey, DetailPageParams } from '../contract';
import { apiMap } from './apiMap';

@injectable()
export class DetailPageSetupData extends IPageSetupData {
  pageKey = DetailPageKey;

  @inject(IPageParseService)
  private pageService: IPageParseService;

  @inject(IProjectService)
  private projectService: IProjectService;

  @postConstruct()
  protected registerApiMap() {
    this.gwApiService.registerApi(apiMap);
  }

  public async getData() {
    const currentPage = this.pageService.currentPage;
    if (!currentPage?.isFirstScreen) {
      return;
    }

    const { simpleName, workItemType, workItemID } = currentPage.params as DetailPageParams;
    const searchParams = currentPage.location.searchParams;
    const work_item_id = Number(workItemID);
    const wi_object_id = Number(workItemID);

    const prefetchDataPromiseQueue: Promise<any>[] = [];

    prefetchDataPromiseQueue.push(
      this.gwApiService.requestWithResponseOnly('APIDemandFetchWorkItem', {
        project_simple_name: simpleName,
        work_item_api_name: workItemType,
        work_item_id,
        modules: [
          {
            key: 'detail',
            params: {
              first_screen: true,
              tab_key: searchParams.get('tabKey') || searchParams.get('tab_key') || undefined,
              node_key: searchParams.get('activeStateId') || undefined,
              visible_area: {
                enable: true,
              },
            },
          },
        ],
        version: '2',
      }),
      this.gwApiService.requestWithResponseOnly('APIFetchMinimalInfoWorkItem', {
        project_simple_name: simpleName,
        work_item_api_name: workItemType,
        work_item_id,
        node_key: searchParams.get('activeStateId') ?? undefined,
      }),
      this.gwApiService.requestWithResponseOnly('APIBatchQueryProjectsBySimpleName', {
        simple_names: [simpleName],
      })
    );

    const projectKey = await this.projectService.fetchProjectKey(simpleName);
    if (!projectKey) {
      return Promise.allSettled(prefetchDataPromiseQueue);
    }

    prefetchDataPromiseQueue.push(
      this.gwApiService.requestWithResponseOnly('APIQueryBusinesses', { project_id: projectKey }),
      this.gwApiService
        .request('APIGetWorkItemTypeInfoV2', {
          project_key: projectKey,
          need_manage_permission: false,
          need_perm_config_permission: false,
        })
        .then((result) => {
          return (result.data.data as { api_name: string; type_key: string }[])?.find(
            (item) => item.api_name === workItemType
          )?.type_key;
        })
        .then((typeKey) => {
          return Promise.allSettled([
            this.gwApiService.requestWithResponseOnly('APIQueryFlowRole', {
              params: [{ project_key: projectKey, work_item_type: typeKey }],
            }),
            this.gwApiService.requestWithResponseOnly(
              'APIQueryTemplateBasicInfosByProjectKeyAndWorkItemTypeKey',
              {
                project_key: projectKey,
                work_item_type: typeKey,
                permission_filter: [],
              }
            ),
            this.gwApiService.requestWithResponseOnly('APIGetWorkItemTypeInfoByProjectKeyAndWITypeKey', {
              project_key: projectKey,
              type_key: typeKey,
              skip_shard_project_key: true,
            }),
            this.gwApiService.requestWithResponseOnly('APIFindBasicWorkItemByIDV2', {
              project_key: projectKey,
              work_item_type: typeKey,
              wi_object_id,
            }),
          ]);
        })
    );

    return Promise.allSettled(prefetchDataPromiseQueue);
  }
}
